import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const MetricsPanel = ({ metrics, loading, recentActivity }) => {
  // Force dark mode for admin components
  useEffect(() => {
    document.documentElement?.setAttribute('data-theme', 'dark');
  }, []);

  const navigate = useNavigate();

  const handleMetricClick = (section) => {
    // Navigate to admin dashboard with specific section parameter
    navigate(`/admin-dashboard?section=${section}`);
  };

  const metricCards = [
    {
      title: 'Total Users',
      value: metrics?.totalUsers,
      change: metrics?.userGrowth,
      icon: 'Users',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      section: 'all-users',
      clickable: true
    },
    {
      title: 'Active Artists',
      value: metrics?.activeArtists,
      change: metrics?.artistGrowth,
      icon: 'Palette',
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      section: 'artists',
      clickable: true
    },
    {
      title: 'Content Editors',
      value: metrics?.editors,
      change: metrics?.editorGrowth,
      icon: 'Edit',
      color: 'text-success',
      bgColor: 'bg-success/10',
      section: 'editors',
      clickable: true
    },
    {
      title: 'System Admins',
      value: metrics?.admins,
      change: metrics?.adminGrowth,
      icon: 'Shield',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      section: 'admins',
      clickable: true
    }
  ];

  const formatChange = (change) => {
    const isPositive = change >= 0;
    return (
      <div className={`flex items-center gap-1 text-xs ${isPositive ? 'text-success' : 'text-destructive'}`}>
        <Icon name={isPositive ? 'TrendingUp' : 'TrendingDown'} size={12} />
        <span>{Math.abs(change)}%</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricCards?.map((metric, index) => (
          <div 
            key={index} 
            onClick={() => metric?.clickable && handleMetricClick(metric?.section)}
            className={`bg-card rounded-lg border border-border p-6 elevation-1 transition-all duration-200 ${
              metric?.clickable 
                ? 'cursor-pointer hover:shadow-md hover:border-primary/20 hover:bg-card/80 active:scale-[0.98]' 
                : ''
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg ${metric?.bgColor} flex items-center justify-center transition-colors duration-200 ${
                metric?.clickable ? 'group-hover:opacity-80' : ''
              }`}>
                <Icon name={metric?.icon} size={24} className={metric?.color} />
              </div>
              {metric?.change !== undefined && formatChange(metric?.change)}
            </div>
            <div>
              <h3 className="text-2xl font-bold text-text-primary mb-1">
                {loading ? (
                  <div className="h-8 bg-muted rounded animate-pulse"></div>
                ) : (
                  (metric?.value || 0)?.toLocaleString()
                )}
              </h3>
              <p className="text-sm text-text-secondary flex items-center gap-1">
                {metric?.title}
                {metric?.clickable && (
                  <Icon name="ExternalLink" size={12} className="text-text-tertiary opacity-50" />
                )}
              </p>
            </div>
            {metric?.clickable && (
              <div className="mt-3 pt-3 border-t border-border/50">
                <p className="text-xs text-text-tertiary">Click to view {metric?.title?.toLowerCase()}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Recent Activity */}
      <div className="bg-card rounded-lg border border-border elevation-1">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-text-primary">Recent Activity</h2>
            <Icon name="Activity" size={20} className="text-text-secondary" />
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {(!recentActivity || recentActivity?.length === 0) ? (
              <p className="text-sm text-text-secondary text-center py-4">No recent activity found.</p>
            ) : (
              recentActivity?.map((activity) => (
                <div key={activity?.id} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    <Icon name="Activity" size={14} className="text-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text-primary">
                      {activity?.user_profiles?.full_name || activity?.user_email || 'Unknown user'} — {activity?.activity_type || activity?.description}
                    </p>
                    <p className="text-xs text-text-secondary mt-1">
                      {activity?.created_at ? new Date(activity.created_at)?.toLocaleString() : ''}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="mt-6 pt-4 border-t border-border">
            <button
              className="text-sm text-accent hover:text-accent/80 font-medium transition-colors"
              onClick={() => window.location.href = '/user-activity-logs'}
            >
              View all activity →
            </button>
          </div>
        </div>
      </div>
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-6 elevation-1">
          <h3 className="text-lg font-semibold text-text-primary mb-4">User Distribution</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-primary rounded-full"></div>
                <span className="text-sm text-text-secondary">Artists</span>
              </div>
              <span className="text-sm font-medium text-text-primary">
                {loading ? (
                  <div className="h-4 w-8 bg-muted rounded animate-pulse"></div>
                ) : (
                  `${metrics?.artistDistribution || 0}%`
                )}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-accent rounded-full"></div>
                <span className="text-sm text-text-secondary">Editors</span>
              </div>
              <span className="text-sm font-medium text-text-primary">
                {loading ? (
                  <div className="h-4 w-8 bg-muted rounded animate-pulse"></div>
                ) : (
                  `${metrics?.editorDistribution || 0}%`
                )}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-success rounded-full"></div>
                <span className="text-sm text-text-secondary">Admins</span>
              </div>
              <span className="text-sm font-medium text-text-primary">
                {loading ? (
                  <div className="h-4 w-8 bg-muted rounded animate-pulse"></div>
                ) : (
                  `${metrics?.adminDistribution || 0}%`
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6 elevation-1">
          <h3 className="text-lg font-semibold text-text-primary mb-4">System Health</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Server Status</span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <span className="text-sm font-medium text-success">Online</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Database</span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <span className="text-sm font-medium text-success">Healthy</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">API Response</span>
              <span className="text-sm font-medium text-text-primary">142ms</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Uptime</span>
              <span className="text-sm font-medium text-text-primary">99.9%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricsPanel;